import { useEffect, useRef, useState } from 'react';
import { haptics } from '../lib/platform';
import { refreshAll } from '../lib/refresh';
import { Icon } from './Icon';
import s from './PullToRefresh.module.css';

/** How far to pull (px, after resistance) before letting go refreshes. */
const TRIGGER = 70;
const MAX = 110;

/**
 * Pull down from the top of a screen to refresh: fetches the latest from the server and, on
 * Android, checks for a newer app. Ignores pulls that start in a map, a sheet or a text field.
 */
export function PullToRefresh() {
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);
  const start = useRef<{ y: number; armed: boolean } | null>(null);
  const pullRef = useRef(0);
  const busyRef = useRef(false);

  useEffect(() => {
    const skip = (t: EventTarget | null) =>
      t instanceof Element &&
      !!t.closest('.maplibregl-map, [role="dialog"], input, textarea, select, [data-no-pull]');
    const onStart = (e: TouchEvent) => {
      if (busyRef.current || e.touches.length !== 1 || window.scrollY > 0 || skip(e.target)) {
        start.current = null;
        return;
      }
      start.current = { y: e.touches[0]!.clientY, armed: false };
    };
    const onMove = (e: TouchEvent) => {
      if (!start.current) return;
      const dy = e.touches[0]!.clientY - start.current.y;
      if (dy <= 0 || window.scrollY > 0) {
        if (pullRef.current) setPull((pullRef.current = 0));
        return;
      }
      // Resistance: the further you pull, the less it moves.
      const d = Math.min(MAX, dy * 0.5);
      if (d >= TRIGGER && !start.current.armed) {
        start.current.armed = true;
        void haptics.tick();
      } else if (d < TRIGGER) start.current.armed = false;
      setPull((pullRef.current = d));
    };
    const onEnd = async () => {
      const go = pullRef.current >= TRIGGER;
      start.current = null;
      if (!go) {
        setPull((pullRef.current = 0));
        return;
      }
      busyRef.current = true;
      setBusy(true);
      setPull((pullRef.current = TRIGGER * 0.8));
      try {
        await Promise.race([refreshAll(), new Promise((r) => setTimeout(r, 8000))]);
      } finally {
        busyRef.current = false;
        setBusy(false);
        setPull((pullRef.current = 0));
      }
    };
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, []);

  if (!pull && !busy) return null;
  return (
    <div
      className={s.puck}
      style={{ transform: `translate(-50%, ${pull - 44}px)`, opacity: Math.min(1, pull / 40) }}
      data-busy={busy || undefined}
      aria-live="polite"
      aria-label={busy ? 'Refreshing' : undefined}
    >
      <span style={busy ? undefined : { transform: `rotate(${(pull / TRIGGER) * 270}deg)` }}>
        <Icon name="arrows-clockwise" size={20} />
      </span>
    </div>
  );
}
